import * as React from "react";
import { useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { Col, Row, SegmentedControl } from "components/bootstrap";
import { DocumentTitle, PageHeader, Spinner } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useMediaQuery from "util/hooks/useMediaQuery";
import AIManagementPageNavigation from "ai-management/AIManagementPageNavigation";
import UsageSearchForm from "ai-management/token-usage/UsageSearchForm";
import UsageStats from "ai-management/token-usage/UsageStats";
import UsageBreakdown from "ai-management/token-usage/UsageBreakdown";
import TokensOverTimeChart from "ai-management/token-usage/TokensOverTimeChart";
import TokenShareChart from "ai-management/token-usage/TokenShareChart";
import UsageLegend from "ai-management/token-usage/UsageLegend";
import { GROUPINGS } from "ai-management/token-usage/breakdown";
import useTokenUsage from "ai-management/token-usage/hooks/useTokenUsage";
import useTokenUsageSearch from "ai-management/token-usage/hooks/useTokenUsageSearch";
import useValueColors from "ai-management/token-usage/hooks/useValueColors";
const GROUPING_OPTIONS = Object.entries(GROUPINGS).map(([value, { label }]) => ({ value, label }));
const Charts = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: ${theme.spacings.lg};

    @media (max-width: ${theme.breakpoints.max.md}) {
      grid-template-columns: minmax(0, 1fr);
    }
  `
);
const SizedByTimeChart = styled.div(
  ({ theme }) => css`
    contain: size;

    @media (max-width: ${theme.breakpoints.max.md}) {
      contain: none;
      margin-top: ${theme.spacings.lg};
    }
  `
);
const SharedLegend = styled.div`
  grid-column: 1 / -1;
`;
const GroupByRow = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacings.sm};
    align-items: center;
    margin-top: ${theme.spacings.sm};
  `
);
const TokenUsagePage = () => {
  var _a;
  const [search, setSearch] = useTokenUsageSearch();
  const [grouping, setGrouping] = useState("feature");
  const { data: usage, isLoading, isFetching } = useTokenUsage(search);
  const colors = useValueColors((_a = usage == null ? void 0 : usage.rows) != null ? _a : [], grouping);
  const theme = useTheme();
  const chartsAreStacked = useMediaQuery(`(max-width: ${theme.breakpoints.max.md})`);
  const sendTelemetry = useSendTelemetry();
  const onSearch = (nextSearch) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AI_MANAGEMENT.TOKEN_USAGE_SEARCHED, {
      app_section: "ai-management-token-usage",
      event_details: {
        timerange_type: nextSearch.timerange.type,
        feature_filter_count: nextSearch.feature_id.length,
        provider_filter_count: nextSearch.provider.length,
        model_filter_count: nextSearch.model.length
      }
    });
    setSearch(nextSearch);
  };
  const onGroupingChange = (nextGrouping) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AI_MANAGEMENT.TOKEN_USAGE_GROUPING_CHANGED, {
      app_section: "ai-management-token-usage",
      event_details: { grouping: nextGrouping }
    });
    setGrouping(nextGrouping);
  };
  return /* @__PURE__ */ React.createElement(DocumentTitle, { title: "AI Token Usage" }, /* @__PURE__ */ React.createElement(AIManagementPageNavigation, null), /* @__PURE__ */ React.createElement(PageHeader, { title: "Token Usage" }, /* @__PURE__ */ React.createElement("span", null, "Review the estimated token consumption of AI features across model providers and models. Token counts come from each provider's own tokenizer and are not directly comparable across providers. Verify actual usage and billing with each provider.")), /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(UsageSearchForm, { search, onSearch, isSearching: isFetching }), /* @__PURE__ */ React.createElement(GroupByRow, null, "Group by", /* @__PURE__ */ React.createElement(SegmentedControl, { data: GROUPING_OPTIONS, value: grouping, onChange: onGroupingChange })))), /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, isLoading || !usage ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(UsageStats, { usage }))), usage && /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(Charts, null, /* @__PURE__ */ React.createElement(
    TokensOverTimeChart,
    {
      usage,
      grouping,
      colors,
      onZoom: (from, to) => setSearch({ ...search, timerange: { type: "absolute", from, to } })
    }
  ), chartsAreStacked && /* @__PURE__ */ React.createElement(UsageLegend, { usage, grouping, colors }), /* @__PURE__ */ React.createElement(SizedByTimeChart, null, /* @__PURE__ */ React.createElement(TokenShareChart, { usage, grouping, colors })), /* @__PURE__ */ React.createElement(SharedLegend, null, /* @__PURE__ */ React.createElement(UsageLegend, { usage, grouping, colors }))))), usage && /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(UsageBreakdown, { usage, grouping, colors }))));
};
export default TokenUsagePage;
